import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Modal } from '../components/ui/Modal';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { usePacientes } from '../hooks/usePacientes';
import { database } from '../config/backend';
import { ref, get as rtdbGet, push, set as rtdbSet, query, orderByChild, limitToLast, onValue, off, equalTo } from '../services/phpDataAdapter';
import { uploadImage, dataURLtoFile } from '../services/imageUploadService';
import { uploadVideo } from '../services/videoUploadService';
import { useHasVideoAccess } from '../hooks/useHasVideoAccess';
import { VideoRecorder } from '../components/video/VideoRecorder';
import { VideoPlayer } from '../components/video/VideoPlayer';
import { LiveStream } from '../components/video/LiveStream';
import { ImageGenerator } from '../components/imageGenerator/ImageGenerator';
import { SocialShareButtons } from '../components/social/SocialShareButtons';
import './Pages.css';

interface CommunityPost {
  id: string;
  authorId: string;
  authorName: string;
  nutritionistId: string;
  content: string;
  imageUrl?: string;
  videoUrl?: string;
  videoThumbnail?: string;
  isLive?: boolean;
  liveStreamId?: string;
  createdAt: string;
  status: 'pending' | 'approved' | 'rejected';
  isPublic: boolean;
  approvedBy?: string | null;
  approvedAt?: string | null;
  reactions: {
    like: string[];
    love: string[];
    support: string[];
  };
  comments: CommunityComment[];
}

interface CommunityComment {
  id: string;
  authorId: string;
  authorName: string;
  content: string;
  createdAt: string;
}

const EMOJI_REACTIONS = [
  { id: 'like', emoji: '👍', label: 'Curtir' },
  { id: 'love', emoji: '❤️', label: 'Amar' },
  { id: 'support', emoji: '💪', label: 'Apoiar' }
];

export const Community: React.FC = () => {
  const { user } = useAuth();
  const { theme, isDark: isDarkMode } = useTheme();
  const { pacientes } = usePacientes();
  const [posts, setPosts] = useState<CommunityPost[]>([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newPostContent, setNewPostContent] = useState('');
  const [selectedPost, setSelectedPost] = useState<CommunityPost | null>(null);
  const [newComment, setNewComment] = useState('');
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [showVideoRecorder, setShowVideoRecorder] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<File | null>(null);
  const [videoPreview, setVideoPreview] = useState<string | null>(null);
  const [showLiveStream, setShowLiveStream] = useState(false);
  const { hasAccess: hasVideoAccess } = useHasVideoAccess();
  const [filter, setFilter] = useState<'all' | 'my' | 'favorites'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [isImageGeneratorOpen, setIsImageGeneratorOpen] = useState(false);
  const [imageErrors, setImageErrors] = useState<Record<string, boolean>>({});
  const [imageLoading, setImageLoading] = useState<Record<string, boolean>>({});
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<string>('');

  useEffect(() => {
    if (user) {
      loadPosts();
      const postsRef = ref(database, 'communityPosts');
      const unsubscribe = onValue(query(postsRef, orderByChild('nutritionistId'), equalTo(user.uid), limitToLast(50)), (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.val();
          // Na web, o usuário é sempre nutricionista, então vê todos os posts
          const postsList: CommunityPost[] = Object.entries(data)
            .map(([id, post]: [string, any]) => {
              // Determinar se é post do nutricionista ou de cliente
              const isPostFromNutritionist = post.authorId === user.uid;

              // Para posts antigos sem os campos, assumir:
              // - Se for do nutricionista: approved e público
              // - Se for de cliente: pending e não público (mais seguro)
              const defaultStatus = isPostFromNutritionist ? 'approved' : 'pending';
              const defaultIsPublic = isPostFromNutritionist;

              return {
                id,
                ...post,
                status: post.status || defaultStatus,
                isPublic: post.isPublic !== undefined ? post.isPublic : defaultIsPublic,
                approvedBy: post.approvedBy || null,
                approvedAt: post.approvedAt || null,
                reactions: post.reactions || { like: [], love: [], support: [] },
                comments: post.comments || []
              };
            })
            .sort((a, b) => {
              // Posts com comentários ficam no topo
              const aHasComments = a.comments && a.comments.length > 0;
              const bHasComments = b.comments && b.comments.length > 0;

              if (aHasComments && !bHasComments) return -1;
              if (!aHasComments && bHasComments) return 1;

              // Se ambos têm comentários, ordenar pela data do comentário mais recente
              if (aHasComments && bHasComments) {
                const aLatestComment = a.comments.reduce((latest: CommunityComment | null, comment: CommunityComment) => {
                  const commentDate = new Date(comment.createdAt).getTime();
                  const latestDate = latest ? new Date(latest.createdAt).getTime() : 0;
                  return commentDate > latestDate ? comment : latest;
                }, null as CommunityComment | null);

                const bLatestComment = b.comments.reduce((latest: CommunityComment | null, comment: CommunityComment) => {
                  const commentDate = new Date(comment.createdAt).getTime();
                  const latestDate = latest ? new Date(latest.createdAt).getTime() : 0;
                  return commentDate > latestDate ? comment : latest;
                }, null as CommunityComment | null);

                if (aLatestComment && bLatestComment) {
                  return new Date(bLatestComment.createdAt).getTime() - new Date(aLatestComment.createdAt).getTime();
                }
              }

              // Se nenhum tem comentários ou ambos têm a mesma data de comentário, ordenar por data de criação
              return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
            });
          setPosts(postsList);
        } else {
          setPosts([]);
        }
      });

      return () => {
        unsubscribe();
      };
    }
  }, [user]);

  const loadPosts = async () => {
    setLoading(true);
    try {
      const postsRef = ref(database, 'communityPosts');
      const snapshot = await rtdbGet(query(postsRef, orderByChild('nutritionistId'), equalTo(user!.uid), limitToLast(50)));
      if (snapshot.exists()) {
        const data = snapshot.val();
        const postsList: CommunityPost[] = Object.entries(data)
          .map(([id, post]: [string, any]) => {
            // Verificar se a URL da imagem é válida
            let imageUrl = post.imageUrl;
            if (imageUrl && typeof imageUrl === 'string') {
              // Garantir que a URL está completa
              if (!imageUrl.startsWith('http://') && !imageUrl.startsWith('https://')) {
                console.warn(`URL de imagem inválida para post ${id}:`, imageUrl);
                imageUrl = undefined;
              }
            }
            // Determinar se é post do nutricionista ou de cliente
            const isPostFromNutritionist = post.authorId === user!.uid;

            // Para posts antigos sem os campos, assumir:
            // - Se for do nutricionista: approved e público
            // - Se for de cliente: pending e não público (mais seguro)
            const defaultStatus = isPostFromNutritionist ? 'approved' : 'pending';
            const defaultIsPublic = isPostFromNutritionist;

            return {
              id,
              ...post,
              imageUrl,
              status: post.status || defaultStatus,
              isPublic: post.isPublic !== undefined ? post.isPublic : defaultIsPublic,
              approvedBy: post.approvedBy || null,
              approvedAt: post.approvedAt || null,
              reactions: post.reactions || { like: [], love: [], support: [] },
              comments: post.comments || []
            };
          })
          .sort((a, b) => {
            // Posts com comentários ficam no topo
            const aHasComments = a.comments && a.comments.length > 0;
            const bHasComments = b.comments && b.comments.length > 0;

            if (aHasComments && !bHasComments) return -1;
            if (!aHasComments && bHasComments) return 1;

            // Se ambos têm comentários, ordenar pela data do comentário mais recente
            if (aHasComments && bHasComments) {
              const aLatestComment = a.comments.reduce((latest: CommunityComment | null, comment: CommunityComment) => {
                const commentDate = new Date(comment.createdAt).getTime();
                const latestDate = latest ? new Date(latest.createdAt).getTime() : 0;
                return commentDate > latestDate ? comment : latest;
              }, null as CommunityComment | null);

              const bLatestComment = b.comments.reduce((latest: CommunityComment | null, comment: CommunityComment) => {
                const commentDate = new Date(comment.createdAt).getTime();
                const latestDate = latest ? new Date(latest.createdAt).getTime() : 0;
                return commentDate > latestDate ? comment : latest;
              }, null as CommunityComment | null);

              if (aLatestComment && bLatestComment) {
                return new Date(bLatestComment.createdAt).getTime() - new Date(aLatestComment.createdAt).getTime();
              }
            }

            // Se nenhum tem comentários ou ambos têm a mesma data de comentário, ordenar por data de criação
            return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
          });
        setPosts(postsList);
        // Resetar erros de imagem ao recarregar posts
        setImageErrors({});
        setImageLoading({});
      } else {
        setPosts([]);
      }
    } catch (error) {
      console.error('Erro ao carregar posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('A imagem deve ter no máximo 5MB');
        return;
      }
      if (!file.type.startsWith('image/')) {
        alert('Por favor, selecione uma imagem válida');
        return;
      }
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageGenerated = async (imageDataUrl: string) => {
    try {
      // Converter data URL para File
      const file = dataURLtoFile(imageDataUrl, `nutrisync-generated-${Date.now()}.png`);

      // Definir como imagem selecionada
      setSelectedImage(file);
      setImagePreview(imageDataUrl);
      setIsImageGeneratorOpen(false);

      alert('Imagem gerada e adicionada ao post!');
    } catch (error) {
      console.error('Erro ao processar imagem gerada:', error);
      alert('Erro ao processar imagem gerada');
    }
  };

  const handleVideoRecordComplete = async (videoBlob: Blob, thumbnail?: string) => {
    if (!user?.uid) return;

    try {
      setUploadingVideo(true);
      setShowVideoRecorder(false);

      // Converter blob para File
      const videoFile = new File([videoBlob], `video_${Date.now()}.webm`, { type: 'video/webm' });

      // Fazer upload
      const { url: videoUrl, thumbnailUrl } = await uploadVideo(videoFile, user.uid, 'community');

      setSelectedVideo(videoFile);
      setVideoPreview(videoUrl || null);
      if (thumbnailUrl) {
        // Thumbnail já está na resposta do upload
      }
    } catch (error: any) {
      console.error('Erro ao processar vídeo:', error);
      alert(error.message || 'Não foi possível processar o vídeo. Tente novamente.');
    } finally {
      setUploadingVideo(false);
    }
  };

  const handleCreatePost = async () => {
    if (!user || !newPostContent.trim()) return;

    setIsUploading(true);
    setUploadProgress('Preparando...');

    try {
      let imageUrl: string | undefined;
      let videoUrl: string | undefined;
      let videoThumbnailUrl: string | undefined;

      // Upload da imagem se houver
      if (selectedImage) {
        try {
          setUploadProgress('Fazendo upload da imagem...');
          console.log('[Community] Iniciando upload da imagem...');

          const uploadResult = await uploadImage(selectedImage, user.uid, 'community');
          imageUrl = uploadResult.url;

          console.log(`[Community] Upload concluído via ${uploadResult.source}: ${imageUrl}`);
          setUploadProgress(`Upload concluído via ${uploadResult.source === 'hostinger' ? 'Servidor' : 'PHP/MySQL'}!`);

          // Verificar se a URL é válida
          if (!imageUrl || (!imageUrl.startsWith('http://') && !imageUrl.startsWith('https://'))) {
            console.error('[Community] URL de imagem inválida gerada:', imageUrl);
            throw new Error('URL de imagem inválida');
          }
        } catch (error: any) {
          console.error('[Community] Erro ao fazer upload da imagem:', error);
          setIsUploading(false);
          setUploadProgress('');
          alert(`Erro ao fazer upload da imagem: ${error.message}\n\nO post será criado sem imagem.`);
          imageUrl = undefined;
        }
      }

      // Upload do vídeo se houver
      if (selectedVideo) {
        try {
          setUploadProgress('Fazendo upload do vídeo...');
          const uploadResult = await uploadVideo(selectedVideo, user.uid, 'community');
          videoUrl = uploadResult.url;
          videoThumbnailUrl = uploadResult.thumbnailUrl;
        } catch (error: any) {
          console.error('[Community] Erro ao fazer upload do vídeo:', error);
          setIsUploading(false);
          setUploadProgress('');
          alert(`Erro ao fazer upload do vídeo: ${error.message}\n\nO post será criado sem vídeo.`);
          videoUrl = undefined;
        }
      }

      setUploadProgress('Publicando post...');
      const postRef = push(ref(database, 'communityPosts'));
      const authorName = pacientes.find(p => p.id === user.uid)?.nome || 'Nutricionista';

      // Na web, o usuário é sempre nutricionista
      await rtdbSet(postRef, {
        authorId: user.uid,
        authorName,
        nutritionistId: user.uid,
        content: newPostContent.trim(),
        imageUrl,
        videoUrl,
        videoThumbnail: videoThumbnailUrl,
        createdAt: new Date().toISOString(),
        status: 'approved',
        isPublic: true,
        approvedBy: user.uid,
        approvedAt: new Date().toISOString(),
        reactions: {
          like: [],
          love: [],
          support: []
        },
        comments: []
      });

      setNewPostContent('');
      setSelectedImage(null);
      setImagePreview(null);
      setSelectedVideo(null);
      setVideoPreview(null);
      setIsModalOpen(false);
      setUploadProgress('');
      setIsUploading(false);
      alert('Post publicado com sucesso!');
    } catch (error) {
      console.error('Erro ao criar post:', error);
      setUploadProgress('');
      setIsUploading(false);
      alert('Erro ao publicar post');
    }
  };

  const handleReaction = async (postId: string, reactionType: 'like' | 'love' | 'support') => {
    if (!user) return;

    const post = posts.find(p => p.id === postId);
    if (!post) return;

    const reactions = { ...post.reactions };
    const reactionArray = reactions[reactionType] || [];
    const hasReacted = reactionArray.includes(user.uid);

    if (hasReacted) {
      reactions[reactionType] = reactionArray.filter(id => id !== user.uid);
    } else {
      reactions[reactionType] = [...reactionArray, user.uid];

      // Criar notificação para o autor do post (se não for o próprio usuário)
      if (post.authorId !== user.uid) {
        const notificationRef = push(ref(database, `notifications/${post.authorId}`));
        await rtdbSet(notificationRef, {
          id: notificationRef.key,
          type: 'reaction',
          postId,
          authorId: user.uid,
          authorName: pacientes.find(p => p.id === user.uid)?.nome || 'Alguém',
          reactionType,
          message: `${pacientes.find(p => p.id === user.uid)?.nome || 'Alguém'} reagiu ao seu post`,
          read: false,
          createdAt: new Date().toISOString()
        });
      }
    }

    try {
      await rtdbSet(ref(database, `communityPosts/${postId}/reactions`), reactions);
    } catch (error) {
      console.error('Erro ao reagir:', error);
    }
  };

  const handleAddComment = async (postId: string) => {
    if (!user || !newComment.trim()) return;

    const post = posts.find(p => p.id === postId);
    if (!post) return;

    const emailName = user.email?.split('@')[0] || '';
    const capitalizedName = emailName.charAt(0).toUpperCase() + emailName.slice(1);
    const authorName = user.displayName || capitalizedName || 'Nutricionista';
    const comment: CommunityComment = {
      id: Date.now().toString(),
      authorId: user.uid,
      authorName,
      content: newComment.trim(),
      createdAt: new Date().toISOString()
    };

    const comments = [...(post.comments || []), comment];

    try {
      await rtdbSet(ref(database, `communityPosts/${postId}/comments`), comments);
      // Atualizar lastActivityAt quando um comentário é adicionado
      await rtdbSet(ref(database, `communityPosts/${postId}/lastActivityAt`), new Date().toISOString());

      // Criar notificação para o autor do post (se não for o próprio usuário)
      if (post.authorId !== user.uid) {
        const notificationRef = push(ref(database, `notifications/${post.authorId}`));
        await rtdbSet(notificationRef, {
          id: notificationRef.key,
          type: 'comment',
          postId,
          commentId: comment.id,
          authorId: user.uid,
          authorName,
          message: `${authorName} comentou no seu post`,
          read: false,
          createdAt: new Date().toISOString()
        });
      }

      setNewComment('');
      setSelectedPost(null);
    } catch (error) {
      console.error('Erro ao adicionar comentário:', error);
      alert('Erro ao adicionar comentário');
    }
  };

  const handleApprovePost = async (post: CommunityPost) => {
    if (!user) return;

    try {
      await rtdbSet(ref(database, `communityPosts/${post.id}/status`), 'approved');
      await rtdbSet(ref(database, `communityPosts/${post.id}/approvedBy`), user.uid);
      await rtdbSet(ref(database, `communityPosts/${post.id}/approvedAt`), new Date().toISOString());
    } catch (error: any) {
      console.error('Erro ao aprovar post:', error);
      alert('Não foi possível aprovar o post. Tente novamente.');
    }
  };

  const handleRejectPost = async (post: CommunityPost) => {
    if (!user) return;

    if (window.confirm('Tem certeza que deseja rejeitar este post?')) {
      try {
        await rtdbSet(ref(database, `communityPosts/${post.id}/status`), 'rejected');
      } catch (error: any) {
        console.error('Erro ao rejeitar post:', error);
        alert('Não foi possível rejeitar o post. Tente novamente.');
      }
    }
  };

  const handleMakePublic = async (post: CommunityPost) => {
    if (!user) return;

    try {
      await rtdbSet(ref(database, `communityPosts/${post.id}/isPublic`), true);
      // Se ainda não estiver aprovado, aprovar também
      if (post.status !== 'approved') {
        await rtdbSet(ref(database, `communityPosts/${post.id}/status`), 'approved');
        await rtdbSet(ref(database, `communityPosts/${post.id}/approvedBy`), user.uid);
        await rtdbSet(ref(database, `communityPosts/${post.id}/approvedAt`), new Date().toISOString());
      }
    } catch (error: any) {
      console.error('Erro ao tornar post público:', error);
      alert('Não foi possível tornar o post público. Tente novamente.');
    }
  };

  const handleDeletePost = async (post: CommunityPost) => {
    if (!user) return;

    // Permite excluir se for o autor do post OU se for nutricionista
    // Na web sempre é nutricionista, mas mantém a lógica para compatibilidade
    const canDelete = post.authorId === user.uid || true;
    if (!canDelete) return;

    if (window.confirm('Tem certeza que deseja excluir este post? Esta ação não pode ser desfeita.')) {
      try {
        await rtdbSet(ref(database, `communityPosts/${post.id}`), null);
      } catch (error: any) {
        console.error('Erro ao excluir post:', error);
        alert('Não foi possível excluir o post. Tente novamente.');
      }
    }
  };

  const filteredPosts = posts.filter(post => {
    const matchesSearch = searchTerm === '' ||
      post.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.authorName.toLowerCase().includes(searchTerm.toLowerCase());

    if (filter === 'my') {
      return matchesSearch && post.authorId === user?.uid;
    }
    if (filter === 'favorites') {
      const hasMyReaction = post.reactions.like?.includes(user?.uid || '') ||
        post.reactions.love?.includes(user?.uid || '') ||
        post.reactions.support?.includes(user?.uid || '');
      return matchesSearch && hasMyReaction;
    }
    return matchesSearch;
  });

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Agora';
    if (minutes < 60) return `${minutes}min atrás`;
    if (hours < 24) return `${hours}h atrás`;
    if (days < 7) return `${days}d atrás`;
    return date.toLocaleDateString('pt-BR');
  };

  return (
    <div className="ns-page">
      <div className="ns-page-header" style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '32px',
        borderRadius: '16px',
        marginBottom: '32px',
        boxShadow: '0 8px 24px rgba(102, 126, 234, 0.2)'
      }}>
        <div>
          <h2 style={{ color: '#ffffff', marginBottom: '8px', fontSize: '28px', fontWeight: '700' }}>🌟 Comunidade NutriSync</h2>
          <p style={{ color: 'rgba(255, 255, 255, 0.95)', fontSize: '15px', margin: 0 }}>Compartilhe seus hábitos alimentares e inspire outros pacientes na jornada por uma vida mais saudável.</p>
        </div>
        <div className="ns-page-header-actions">
          <Button
            onClick={() => setIsModalOpen(true)}
            style={{
              background: '#ffffff',
              color: '#667eea',
              fontWeight: '600',
              padding: '12px 24px',
              fontSize: '15px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              border: 'none'
            }}
          >
            ✍️ Novo Post
          </Button>
        </div>
      </div>

      <Card title="Feed da Comunidade" subtitle="Posts recentes da comunidade">
        <div style={{ marginBottom: '24px', display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ flex: 1, minWidth: '200px', position: 'relative' }}>
            <span style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', fontSize: '18px', color: theme.colors.textMuted }}>🔍</span>
            <input
              type="text"
              className="ns-input"
              placeholder="Buscar posts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ paddingLeft: '44px' }}
            />
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => setFilter('all')}
              style={{
                padding: '10px 18px',
                fontSize: '13px',
                fontWeight: '600',
                background: filter === 'all' ? 'linear-gradient(135deg, #667eea, #764ba2)' : theme.colors.surface,
                color: filter === 'all' ? '#ffffff' : theme.colors.textMuted,
                border: filter === 'all' ? 'none' : `2px solid ${theme.colors.border}`,
                borderRadius: '10px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: filter === 'all' ? '0 4px 12px rgba(102, 126, 234, 0.3)' : 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              <span>🌐</span> Todos
            </button>
            <button
              type="button"
              onClick={() => setFilter('my')}
              style={{
                padding: '10px 18px',
                fontSize: '13px',
                fontWeight: '600',
                background: filter === 'my' ? 'linear-gradient(135deg, #667eea, #764ba2)' : theme.colors.surface,
                color: filter === 'my' ? '#ffffff' : theme.colors.textMuted,
                border: filter === 'my' ? 'none' : `2px solid ${theme.colors.border}`,
                borderRadius: '10px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: filter === 'my' ? '0 4px 12px rgba(102, 126, 234, 0.3)' : 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              <span>👤</span> Meus Posts
            </button>
            <button
              type="button"
              onClick={() => setFilter('favorites')}
              style={{
                padding: '10px 18px',
                fontSize: '13px',
                fontWeight: '600',
                background: filter === 'favorites' ? 'linear-gradient(135deg, #667eea, #764ba2)' : theme.colors.surface,
                color: filter === 'favorites' ? '#ffffff' : theme.colors.textMuted,
                border: filter === 'favorites' ? 'none' : `2px solid ${theme.colors.border}`,
                borderRadius: '10px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: filter === 'favorites' ? '0 4px 12px rgba(102, 126, 234, 0.3)' : 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              <span>⭐</span> Com Reações
            </button>
          </div>
        </div>

        {loading ? (
          <p>Carregando posts...</p>
        ) : filteredPosts.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: theme.colors.textMuted }}>
            <p>Nenhum post ainda. Seja o primeiro a compartilhar!</p>
            <Button onClick={() => setIsModalOpen(true)} style={{ marginTop: '16px' }}>
              Criar Primeiro Post
            </Button>
          </div>
        ) : (
          <div className="ns-community-feed">
            {filteredPosts.map((post: CommunityPost) => {
              const userReactions = {
                like: post.reactions.like?.includes(user?.uid || '') || false,
                love: post.reactions.love?.includes(user?.uid || '') || false,
                support: post.reactions.support?.includes(user?.uid || '') || false
              };

              return (
                <div
                  key={post.id}
                  className="ns-community-post-card"
                  style={{
                    padding: post.imageUrl ? '0' : '20px'
                  }}
                >
                  {post.videoUrl && (
                    <div className="ns-community-post-image-container" style={{ padding: '0' }}>
                      <VideoPlayer
                        videoUrl={post.videoUrl}
                        thumbnailUrl={post.videoThumbnail}
                        controls
                        style={{ width: '100%' }}
                      />
                    </div>
                  )}
                  {post.imageUrl && !imageErrors[post.id] && !post.videoUrl && (
                    <div className="ns-community-post-image-container">
                      {imageLoading[post.id] && (
                        <div className="ns-community-image-loading">
                          Carregando imagem...
                        </div>
                      )}
                      <img
                        src={post.imageUrl}
                        alt="Post"
                        loading="lazy"
                        onLoad={() => {
                          console.log(`Imagem carregada com sucesso para post ${post.id}`);
                          setImageLoading(prev => ({ ...prev, [post.id]: false }));
                        }}
                        onError={(e) => {
                          console.error('Erro ao carregar imagem do post:', post.id, 'URL:', post.imageUrl);
                          setImageErrors(prev => ({ ...prev, [post.id]: true }));
                          setImageLoading(prev => ({ ...prev, [post.id]: false }));

                          // Tentar recarregar a imagem após um delay
                          const img = e.currentTarget;
                          const originalSrc = img.src;
                          let retryCount = 0;
                          const maxRetries = 3;

                          const retryLoad = () => {
                            if (retryCount < maxRetries) {
                              retryCount++;
                              console.log(`Tentativa ${retryCount} de recarregar imagem do post ${post.id}`);
                              img.src = '';
                              setTimeout(() => {
                                img.src = originalSrc + (originalSrc.includes('?') ? '&' : '?') + `retry=${retryCount}&t=${Date.now()}`;
                              }, 1000 * retryCount);
                            }
                          };

                          setTimeout(retryLoad, 2000);
                        }}
                        onLoadStart={() => {
                          setImageLoading(prev => ({ ...prev, [post.id]: true }));
                        }}
                        className="ns-community-post-image"
                        style={{
                          display: imageLoading[post.id] ? 'none' : 'block'
                        }}
                        onClick={() => {
                          if (post.imageUrl) {
                            window.open(post.imageUrl, '_blank');
                          }
                        }}
                        title="Clique para abrir imagem em tamanho real"
                      />
                    </div>
                  )}

                  <div style={{ padding: '20px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                          <div style={{ fontWeight: 600, fontSize: '14px', color: theme.colors.text }}>
                            {post.authorId === user?.uid ? 'Nutricionista' : (pacientes.find(p => p.id === post.authorId)?.nome || post.authorName)}
                          </div>
                          {post.status === 'pending' && (
                            <span style={{
                              padding: '4px 8px',
                              borderRadius: '12px',
                              backgroundColor: '#ffa500',
                              color: '#fff',
                              fontSize: '10px',
                              fontWeight: '600'
                            }}>
                              Pendente
                            </span>
                          )}
                          {post.status === 'rejected' && (
                            <span style={{
                              padding: '4px 8px',
                              borderRadius: '12px',
                              background: 'var(--theme-danger-solid)',
                              color: '#fff',
                              fontSize: '10px',
                              fontWeight: '600'
                            }}>
                              Rejeitado
                            </span>
                          )}
                          {post.status === 'approved' && post.isPublic && (
                            <span style={{
                              padding: '4px 8px',
                              borderRadius: '12px',
                              backgroundColor: '#10b981',
                              color: '#fff',
                              fontSize: '10px',
                              fontWeight: '600'
                            }}>
                              Público
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: '12px', color: theme.colors.textMuted }}>
                          {formatDate(post.createdAt)}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleDeletePost(post)}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: 'var(--theme-danger)',
                          cursor: 'pointer',
                          padding: '4px 8px',
                          fontSize: '18px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                        title="Excluir post"
                      >
                        🗑️
                      </button>
                    </div>

                    {/* Botões de Moderação (apenas para posts de clientes) */}
                    {post.authorId !== user?.uid && (post.status === 'pending' || (post.status === 'approved' && !post.isPublic)) && (
                      <div style={{
                        display: 'flex',
                        gap: '8px',
                        marginBottom: '12px',
                        paddingTop: '12px',
                        borderTop: `1px solid ${theme.colors.border}`
                      }}>
                        {post.status === 'pending' && (
                          <>
                            <Button
                              onClick={() => handleApprovePost(post)}
                              style={{
                                flex: 1,
                                fontSize: '12px',
                                padding: '6px 12px',
                                background: '#10b981',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer'
                              }}
                            >
                              Aprovar
                            </Button>
                            <Button
                              onClick={() => handleRejectPost(post)}
                              style={{
                                flex: 1,
                                fontSize: '12px',
                                padding: '6px 12px',
                                background: 'transparent',
                                color: 'var(--theme-danger)',
                                border: '1px solid #ef4444',
                                borderRadius: '6px',
                                cursor: 'pointer'
                              }}
                            >
                              Rejeitar
                            </Button>
                          </>
                        )}
                        {post.status === 'approved' && !post.isPublic && (
                          <Button
                            onClick={() => handleMakePublic(post)}
                            style={{
                              flex: 1,
                              fontSize: '12px',
                              padding: '6px 12px',
                              background: '#0ea5e9',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer'
                            }}
                          >
                            Tornar Público
                          </Button>
                        )}
                      </div>
                    )}

                    <div style={{ fontSize: '14px', color: theme.colors.text, marginBottom: '16px', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>
                      {post.content}
                    </div>
                  </div>

                  {post.imageUrl && imageErrors[post.id] && (
                    <div className="ns-community-post-image-container" style={{
                      background: theme.colors.surface,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '20px',
                      color: theme.colors.textMuted
                    }}>
                      <div style={{ fontSize: '48px', marginBottom: '12px' }}>🖼️</div>
                      <div style={{ fontSize: '14px', textAlign: 'center', marginBottom: '8px' }}>
                        Imagem não pôde ser carregada
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setImageErrors(prev => {
                            const newErrors = { ...prev };
                            delete newErrors[post.id];
                            return newErrors;
                          });
                          setImageLoading(prev => ({ ...prev, [post.id]: true }));
                        }}
                        style={{
                          padding: '6px 12px',
                          fontSize: '12px',
                          background: '#0ea5e9',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer'
                        }}
                      >
                        Tentar novamente
                      </button>
                    </div>
                  )}

                  {!post.imageUrl && (
                    <div style={{ padding: '20px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            <div style={{ fontWeight: 600, fontSize: '14px', color: theme.colors.text }}>
                              {post.authorId === user?.uid ? 'Nutricionista' : (pacientes.find(p => p.id === post.authorId)?.nome || post.authorName)}
                            </div>
                            {post.status === 'pending' && (
                              <span style={{
                                padding: '4px 8px',
                                borderRadius: '12px',
                                backgroundColor: '#ffa500',
                                color: '#fff',
                                fontSize: '10px',
                                fontWeight: '600'
                              }}>
                                Pendente
                              </span>
                            )}
                            {post.status === 'rejected' && (
                              <span style={{
                                padding: '4px 8px',
                                borderRadius: '12px',
                                background: 'var(--theme-danger-solid)',
                                color: '#fff',
                                fontSize: '10px',
                                fontWeight: '600'
                              }}>
                                Rejeitado
                              </span>
                            )}
                            {post.status === 'approved' && post.isPublic && (
                              <span style={{
                                padding: '4px 8px',
                                borderRadius: '12px',
                                backgroundColor: '#10b981',
                                color: '#fff',
                                fontSize: '10px',
                                fontWeight: '600'
                              }}>
                                Público
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: '12px', color: theme.colors.textMuted }}>
                            {formatDate(post.createdAt)}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleDeletePost(post)}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            color: 'var(--theme-danger)',
                            cursor: 'pointer',
                            padding: '4px 8px',
                            fontSize: '18px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          title="Excluir post"
                        >
                          🗑️
                        </button>
                      </div>

                      {/* Botões de Moderação (apenas para posts de clientes) */}
                      {post.authorId !== user?.uid && (post.status === 'pending' || (post.status === 'approved' && !post.isPublic)) && (
                        <div style={{
                          display: 'flex',
                          gap: '8px',
                          marginBottom: '12px',
                          paddingTop: '12px',
                          borderTop: `1px solid ${theme.colors.border}`
                        }}>
                          {post.status === 'pending' && (
                            <>
                              <Button
                                onClick={() => handleApprovePost(post)}
                                style={{
                                  flex: 1,
                                  fontSize: '12px',
                                  padding: '6px 12px',
                                  background: '#10b981',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: 'pointer'
                                }}
                              >
                                Aprovar
                              </Button>
                              <Button
                                onClick={() => handleRejectPost(post)}
                                style={{
                                  flex: 1,
                                  fontSize: '12px',
                                  padding: '6px 12px',
                                  background: 'transparent',
                                  color: 'var(--theme-danger)',
                                  border: '1px solid #ef4444',
                                  borderRadius: '6px',
                                  cursor: 'pointer'
                                }}
                              >
                                Rejeitar
                              </Button>
                            </>
                          )}
                          {post.status === 'approved' && !post.isPublic && (
                            <Button
                              onClick={() => handleMakePublic(post)}
                              style={{
                                flex: 1,
                                fontSize: '12px',
                                padding: '6px 12px',
                                background: '#0ea5e9',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer'
                              }}
                            >
                              Tornar Público
                            </Button>
                          )}
                        </div>
                      )}

                      <div style={{ fontSize: '14px', color: theme.colors.text, marginBottom: '16px', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>
                        {post.content}
                      </div>
                    </div>
                  )}

                  <div style={{ padding: post.imageUrl ? '20px' : '0', paddingTop: post.imageUrl && !imageErrors[post.id] ? '20px' : '0' }}>
                    {/* Reactions */}
                    <div style={{ display: 'flex', gap: '16px', marginBottom: '12px', paddingBottom: '12px', borderBottom: '1px solid #e2e8f0' }}>
                      {EMOJI_REACTIONS.map(reaction => {
                        const count = post.reactions[reaction.id as keyof typeof post.reactions]?.length || 0;
                        const isActive = userReactions[reaction.id as keyof typeof userReactions];

                        return (
                          <button
                            key={reaction.id}
                            type="button"
                            onClick={() => handleReaction(post.id, reaction.id as 'like' | 'love' | 'support')}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              padding: '6px 12px',
                              background: isActive ? (isDarkMode ? 'rgba(59, 130, 246, 0.2)' : '#eff6ff') : theme.colors.surface,
                              border: `1px solid ${isActive ? '#0ea5e9' : theme.colors.border}`,
                              borderRadius: '20px',
                              cursor: 'pointer',
                              fontSize: '13px',
                              transition: 'all 0.2s ease'
                            }}
                            title={reaction.label}
                          >
                            <span style={{ fontSize: '16px' }}>{reaction.emoji}</span>
                            <span style={{ color: theme.colors.textMuted }}>{count}</span>
                          </button>
                        );
                      })}
                    </div>

                    {/* Botões de Compartilhamento (apenas para posts com imagem) */}
                    {post.imageUrl && !imageErrors[post.id] && (
                      <div style={{
                        marginBottom: '12px',
                        paddingBottom: '12px',
                        borderBottom: `1px solid ${theme.colors.border}`,
                        display: 'flex',
                        justifyContent: 'flex-end'
                      }}>
                        <SocialShareButtons
                          imageUrl={post.imageUrl}
                          postText={post.content}
                          postId={post.id}
                        />
                      </div>
                    )}

                    {/* Comments */}
                    {post.comments && post.comments.length > 0 && (
                      <div style={{ marginBottom: '12px' }}>
                        <div style={{ fontSize: '12px', fontWeight: 600, color: theme.colors.textMuted, marginBottom: '8px' }}>
                          Comentários ({post.comments.length})
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {post.comments.slice(0, 3).map((comment: CommunityComment) => (
                            <div key={comment.id} style={{ padding: '8px', background: theme.colors.surface, borderRadius: '6px' }}>
                              <div style={{ fontSize: '12px', fontWeight: 600, color: theme.colors.text, marginBottom: '4px' }}>
                                {comment.authorName}
                              </div>
                              <div style={{ fontSize: '13px', color: theme.colors.textMuted }}>
                                {comment.content}
                              </div>
                              <div style={{ fontSize: '11px', color: theme.colors.textMuted, marginTop: '4px' }}>
                                {formatDate(comment.createdAt)}
                              </div>
                            </div>
                          ))}
                          {post.comments.length > 3 && (
                            <Button
                              variant="secondary"
                              onClick={() => setSelectedPost(post)}
                              style={{ fontSize: '12px', padding: '4px 8px', alignSelf: 'flex-start' }}
                            >
                              Ver todos os comentários ({post.comments.length})
                            </Button>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Add Comment */}
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <input
                        type="text"
                        className="ns-input"
                        placeholder="Escreva um comentário..."
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter' && newComment.trim()) {
                            handleAddComment(post.id);
                          }
                        }}
                        style={{ flex: 1, fontSize: '13px' }}
                      />
                      <Button
                        variant="secondary"
                        onClick={() => handleAddComment(post.id)}
                        disabled={!newComment.trim()}
                        style={{ fontSize: '12px', padding: '8px 16px' }}
                      >
                        Comentar
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Create Post Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          if (!isUploading) {
            setIsModalOpen(false);
            setNewPostContent('');
            setSelectedImage(null);
            setImagePreview(null);
          }
        }}
        title="Criar Novo Post"
        footer={
          <>
            <Button variant="secondary" onClick={() => setIsModalOpen(false)} disabled={isUploading}>
              Cancelar
            </Button>
            <Button onClick={handleCreatePost} disabled={!newPostContent.trim() || isUploading}>
              {isUploading ? 'Publicando...' : 'Publicar'}
            </Button>
          </>
        }
      >
        <div>
          {isUploading && (
            <div style={{
              padding: '16px',
              marginBottom: '16px',
              background: '#eff6ff',
              border: '1px solid #3b82f6',
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <div style={{
                display: 'inline-block',
                width: '24px',
                height: '24px',
                border: '3px solid #3b82f6',
                borderTop: '3px solid transparent',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                marginBottom: '8px'
              }}></div>
              <p style={{ margin: 0, color: '#1e40af', fontWeight: 500 }}>
                {uploadProgress}
              </p>
            </div>
          )}

          <label className="ns-field">
            <span>Compartilhe seus hábitos alimentares</span>
            <textarea
              className="ns-textarea"
              rows={6}
              placeholder="Ex: Hoje experimentei uma receita nova de salada com quinoa e fiquei surpreso com o sabor! Recomendo para todos que estão buscando uma alimentação mais saudável..."
              value={newPostContent}
              onChange={(e) => setNewPostContent(e.target.value)}
              disabled={isUploading}
            />
          </label>

          <div style={{ marginTop: '16px' }}>
            <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
              <Button
                variant="secondary"
                onClick={() => setIsImageGeneratorOpen(true)}
                style={{
                  fontSize: '14px',
                  padding: '12px 20px',
                  background: 'linear-gradient(135deg, #667eea, #764ba2)',
                  color: '#ffffff',
                  border: 'none',
                  fontWeight: '600',
                  boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                disabled={isUploading}
              >
                <span style={{ fontSize: '18px' }}>🎨</span> Gerar Imagem Personalizada
              </Button>
              {hasVideoAccess && (
                <>
                  <Button
                    variant="secondary"
                    onClick={() => {
                      if (hasVideoAccess) {
                        setShowVideoRecorder(true);
                      } else {
                        alert('Esta funcionalidade está disponível apenas para plano intermediário ou superior. Faça upgrade do seu plano.');
                      }
                    }}
                    style={{
                      fontSize: '14px',
                      padding: '12px 20px',
                      background: 'linear-gradient(135deg, #f093fb, #f5576c)',
                      color: '#ffffff',
                      border: 'none',
                      fontWeight: '600',
                      boxShadow: '0 4px 12px rgba(245, 87, 108, 0.3)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                    disabled={isUploading || uploadingVideo}
                  >
                    <span style={{ fontSize: '18px' }}>🎥</span> {uploadingVideo ? 'Processando...' : 'Gravar Vídeo'}
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => {
                      if (hasVideoAccess) {
                        setShowLiveStream(true);
                      } else {
                        alert('Esta funcionalidade está disponível apenas para plano intermediário ou superior. Faça upgrade do seu plano.');
                      }
                    }}
                    style={{
                      fontSize: '14px',
                      padding: '12px 20px',
                      background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                      color: '#ffffff',
                      border: 'none',
                      fontWeight: '600',
                      boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                    disabled={isUploading || uploadingVideo}
                  >
                    <span style={{ fontSize: '18px' }}>🔴</span> Transmissão Ao Vivo
                  </Button>
                </>
              )}
            </div>
            <label className="ns-field">
              <span>Ou Adicionar Imagem Manualmente (Opcional)</span>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageSelect}
                style={{ fontSize: '13px' }}
                disabled={isUploading}
              />
            </label>
            {imagePreview && (
              <div style={{ marginTop: '12px', position: 'relative', width: '100%', maxWidth: '600px' }}>
                <div style={{
                  width: '100%',
                  maxHeight: '400px',
                  overflow: 'hidden',
                  borderRadius: '12px',
                  border: '2px solid #e2e8f0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: theme.colors.surface,
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                }}>
                  <img
                    src={imagePreview}
                    alt="Preview"
                    style={{
                      width: '100%',
                      height: 'auto',
                      maxHeight: '400px',
                      objectFit: 'contain',
                      borderRadius: '12px'
                    }}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedImage(null);
                    setImagePreview(null);
                  }}
                  style={{
                    position: 'absolute',
                    top: '8px',
                    right: '8px',
                    background: 'var(--theme-danger-solid)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '50%',
                    width: '32px',
                    height: '32px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#dc2626';
                    e.currentTarget.style.transform = 'scale(1.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#ef4444';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                >
                  ✕
                </button>
              </div>
            )}
          </div>

          <p style={{ fontSize: '12px', color: theme.colors.textMuted, marginTop: '8px' }}>
            Compartilhe suas experiências, receitas, dicas e conquistas com a comunidade!
          </p>
        </div>
      </Modal>

      {/* Comments Modal */}
      {selectedPost && (
        <Modal
          isOpen={!!selectedPost}
          onClose={() => setSelectedPost(null)}
          title={`Comentários - ${selectedPost.authorName}`}
          footer={
            <Button variant="secondary" onClick={() => setSelectedPost(null)}>
              Fechar
            </Button>
          }
        >
          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {selectedPost.comments.map(comment => (
              <div key={comment.id} style={{ padding: '12px', marginBottom: '12px', background: theme.colors.surface, borderRadius: '8px' }}>
                <div style={{ fontSize: '13px', fontWeight: 600, color: theme.colors.text, marginBottom: '4px' }}>
                  {comment.authorName}
                </div>
                <div style={{ fontSize: '14px', color: theme.colors.textMuted, marginBottom: '4px' }}>
                  {comment.content}
                </div>
                <div style={{ fontSize: '11px', color: theme.colors.textMuted }}>
                  {formatDate(comment.createdAt)}
                </div>
              </div>
            ))}
          </div>
        </Modal>
      )}

      {/* Image Generator Modal */}
      <ImageGenerator
        isOpen={isImageGeneratorOpen}
        onClose={() => setIsImageGeneratorOpen(false)}
        onImageGenerated={handleImageGenerated}
      />

      {/* Modal de Gravação de Vídeo */}
      <VideoRecorder
        isOpen={showVideoRecorder}
        onClose={() => setShowVideoRecorder(false)}
        onRecordComplete={handleVideoRecordComplete}
        maxDuration={300}
      />

      {/* Modal de Transmissão Ao Vivo */}
      <LiveStream
        isOpen={showLiveStream}
        onClose={() => setShowLiveStream(false)}
      />

      {/* CSS da animação de loading */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

